import React, {useEffect, useState} from 'react'
import styled from '@emotion/styled'
import {keyframes} from '@emotion/react'
import dayjs from 'dayjs'

const STEPS = [
  'Gathering Your Account Details',
  'Creating Your Free My Account',
  'Go Time!',
]

// Same palette as the OOE Account Management theme.
const BLUE = '#0070f3'
const BORDER = '#e0e0e0'

const spin = keyframes`
  to { transform: rotate(360deg); }
`

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(0, 0, 0, 0.45);
`

const Dialog = styled.div`
  width: 100%;
  max-width: 400px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow:
    #00000029 0 8px 24px,
    #0000004d 0 4px 8px;
  overflow: hidden;
`

const Header = styled.div`
  padding: 24px;
  border-bottom: 1px solid ${BORDER};
`

// Same heading style as the rest of ooecp (e.g. .verify-token-title).
const Title = styled.h2`
  margin: 0;
  font-family: 'Montserrat', sans-serif;
  font-size: 24px;
  font-weight: 600;
  line-height: 1.3;
  color: #000000;
`

const Body = styled.div`
  padding: 24px;
`

const StepList = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
`

const Step = styled.li`
  position: relative;
  display: flex;
  gap: 12px;
  padding-bottom: 20px;

  &:last-of-type {
    padding-bottom: 0;
  }

  /* connector to the next step */
  &:not(:last-of-type)::before {
    content: '';
    position: absolute;
    left: 11px;
    top: 24px;
    bottom: 0;
    width: 2px;
    background: ${({done}) => (done ? BLUE : BORDER)};
  }
`

const Marker = styled.span`
  flex: 0 0 24px;
  width: 24px;
  height: 24px;
  box-sizing: border-box;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  background: ${({done}) => (done ? BLUE : '#ffffff')};
  border: 2px solid ${({pending}) => (pending ? BORDER : BLUE)};

  svg {
    width: 12px;
    height: 12px;
  }
`

const ActiveMarker = styled(Marker)`
  border-right-color: transparent;
  animation: ${spin} 0.75s linear infinite;
`

const StepText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-top: 2px;
`

const StepTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${({pending}) => (pending ? '#aaaaaa' : '#000000')};
`

const StepTime = styled.span`
  font-size: 12px;
  color: #666666;
`

const CheckIcon = () => (
  <svg viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <path
      d="M2.5 6.5l2.2 2.2L9.5 3.8"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

// "Creating Account" progress modal shown on the payment page while the
// account is set up (after the customer verifies the OTP; also the 1:Many
// link's account creation). `currentStep` is the index of the step in
// progress — earlier steps show as done, later ones as pending.
const LoadingTimelineModal = ({currentStep = 1}) => {
  // When each step was first reached, shown under its title.
  const [stamps, setStamps] = useState(() => [dayjs(), null, null])

  useEffect(() => {
    setStamps(prev =>
      prev.map((stamp, i) => (stamp || i > currentStep ? stamp : dayjs())),
    )
  }, [currentStep])

  const activeIndex = Math.min(currentStep, STEPS.length - 1)

  return (
    <Backdrop role="presentation">
      <Dialog role="dialog" aria-modal="true" aria-label="Creating Account">
        <Header>
          <Title>Creating Account</Title>
        </Header>
        <Body>
          <StepList>
            {STEPS.map((title, i) => {
              const done = i < activeIndex
              const active = i === activeIndex
              const pending = i > activeIndex
              const MarkerEl = active ? ActiveMarker : Marker
              return (
                <Step key={title} done={done}>
                  <MarkerEl done={done} pending={pending}>
                    {done && <CheckIcon />}
                  </MarkerEl>
                  <StepText>
                    <StepTitle pending={pending}>{title}</StepTitle>
                    {stamps[i] && (
                      <StepTime>
                        {stamps[i].format('MMMM D [at] h:mm A')}
                      </StepTime>
                    )}
                  </StepText>
                </Step>
              )
            })}
          </StepList>
        </Body>
      </Dialog>
    </Backdrop>
  )
}

export {LoadingTimelineModal}
